import React, { useMemo, useState } from 'react';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useFiles } from '../contexts/FilesContext';
import { useAuth } from '../contexts/AuthContext';
import { isFeatureEnabled } from '../config/accountSettings';
import { FileUploadState, InvoiceData } from '../types/invoice';
import { OCRApiService } from '../services/api';
import ErrorModal from './ErrorModal';
import ConfirmModal from './ConfirmModal';
import ExportModal from './ExportModal';
import toast from 'react-hot-toast';

type FilterTab = 'pending' | 'completed' | 'error';

/**
 * Document sidebar — upload button + filterable file list with checkboxes for bulk actions.
 */
interface DocumentSidebarProps {
  onProcessFiles?: () => void;
}

const DocumentSidebar: React.FC<DocumentSidebarProps> = ({ onProcessFiles }) => {
  const {
    files,
    invoices,
    removeFile,
    selectedFileId,
    setSelectedFileId,
    deleteInvoice,
    invoicesLoading,
    checkedIds,
    setCheckedIds,
    sidebarTab,
    setSidebarTab,
    formDirtyRef,
    formSaveRef,
  } = useFiles();

  const { user } = useAuth();
  const isStateTaxEnabled = isFeatureEnabled(user?.account, 'state_tax_code');

  const filter = sidebarTab as FilterTab;
  const setFilter = (tab: FilterTab) => setSidebarTab(tab);
  const [errorModal, setErrorModal] = useState<{ title: string; errors: string[] } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ count: number; action: () => void } | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState<{ targetId: string } | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState<'csv' | 'txt' | null>(null);
  type PendingExport =
    | { intent: 'open-modal' }
    | { intent: 'run'; format: 'csv' | 'txt' };
  const [pendingExport, setPendingExport] = useState<PendingExport | null>(null);

  // Combine pending + completed into one list
  const allDocuments = useMemo(() => {
    const pending = files.map(f => ({ ...f, source: 'pending' as const }));
    const completed = invoices.map(f => ({ ...f, source: 'invoice' as const }));
    return [...pending, ...completed];
  }, [files, invoices]);

  const filteredDocuments = useMemo(() => {
    if (filter === 'pending') return allDocuments.filter(d => d.status === 'pending' || d.status === 'processing');
    if (filter === 'completed') return allDocuments.filter(d => d.status === 'completed');
    if (filter === 'error') return allDocuments.filter(d => d.status === 'error');
    return allDocuments;
  }, [allDocuments, filter]);

  // Checkbox handlers
  const toggleCheck = (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const selectableDocs = filteredDocuments.filter(d => d.status !== 'processing');
  const hasProcessing = filteredDocuments.some(d => d.status === 'processing');
  const allChecked = selectableDocs.length > 0 && selectableDocs.every(d => checkedIds.has(d.id));

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(selectableDocs.map(d => d.id)));
    }
  };

  // Mirrors backend validate_invoice_data — surfaces errors before opening the export modal
  const validateExportData = (invoices: InvoiceData[]): string[] => {
    const errors: string[] = [];
    for (const inv of invoices) {
      const invNum = inv.invoice_number || 'unknown';
      const poNumber = (inv.po_number || '').trim();
      const lineItems = inv.line_items || [];
      const hasTax = Number(inv.total_tax || 0) > 0;

      if (lineItems.length === 0) {
        if (!poNumber) {
          errors.push(`Invoice "${invNum}": Needs a PO Number or GL ID on each line item.`);
        }
        continue;
      }

      lineItems.forEach((li, i) => {
        const glId = (li.gl_id || '').trim();
        const taxCode = (li.tax_code || '').trim();
        const label = `Invoice "${invNum}", line ${i + 1}`;
        // Each line needs at least one of PO Number or GL ID. They are
        // not mutually exclusive, a line with both is fine (the GL
        // overrides or supplements the PO's default). Mirrors
        // export_routes.py validate_invoice_data.
        if (!poNumber && !glId) {
          errors.push(`${label}: Needs a PO Number or GL ID.`);
        } else if (glId.toUpperCase() === 'TBD') {
          errors.push(`${label}: GL ID is still TBD, enter the real account number.`);
        }

        if (isStateTaxEnabled && hasTax && !taxCode) {
          errors.push(`${label}: Invoice has sales tax, enter a Tax Code under Show More.`);
        }
      });
    }
    return errors;
  };

  const getSelectedInvoiceData = (): InvoiceData[] => {
    const selected = allDocuments.filter(d => checkedIds.has(d.id) && d.status === 'completed');
    return selected
      .map(f => f.editedData || f.result?.data)
      .filter((data): data is InvoiceData => data !== undefined);
  };

  const handleOpenExportModal = () => {
    const invoiceDataList = getSelectedInvoiceData();
    if (invoiceDataList.length === 0) {
      toast.error('No completed invoices selected to export');
      return;
    }
    // Unsaved changes: save before validating so what's validated matches what exports
    if (formDirtyRef.current) {
      setPendingExport({ intent: 'open-modal' });
      return;
    }
    const errors = validateExportData(invoiceDataList);
    if (errors.length > 0) {
      setErrorModal({ title: 'Cannot Export', errors });
      return;
    }
    setIsExportModalOpen(true);
  };

  const handleExportFromModal = async (format: 'csv' | 'txt') => {
    if (formDirtyRef.current) {
      setIsExportModalOpen(false);
      setPendingExport({ intent: 'run', format });
      return;
    }
    const invoiceDataList = getSelectedInvoiceData();
    if (invoiceDataList.length === 0) {
      toast.error('No completed invoices selected to export');
      return;
    }

    setIsExporting(format);
    try {
      const blob = format === 'txt'
        ? await OCRApiService.exportKeystoneTXT(invoiceDataList)
        : await OCRApiService.exportKeystoneCSV(invoiceDataList);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `keystone_import_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${invoiceDataList.length} invoice(s)`);
      setIsExportModalOpen(false);
    } catch (error: any) {
      if (error?.response?.status === 422) {
        const validationErrors = error.response?.data?.detail?.validation_errors;
        if (Array.isArray(validationErrors)) {
          const cleanErrors = validationErrors.map((e: string) =>
            e.replace('No PO Number and no line items with GL ID.', 'Needs a PO Number or GL ID on each line item.')
          );
          setErrorModal({ title: 'Cannot Export', errors: cleanErrors });
        } else {
          setErrorModal({ title: 'Cannot Export', errors: ['Could not export. Please check your invoice data and try again.'] });
        }
      } else {
        toast.error('Export failed. Please try again.');
      }
    } finally {
      setIsExporting(null);
    }
  };

  const handleSaveAndContinueExport = async () => {
    const pending = pendingExport;
    if (!pending) return;
    setPendingExport(null);
    if (formSaveRef.current) {
      try {
        await formSaveRef.current();
      } catch {
        toast.error('Failed to save changes. Export cancelled.');
        return;
      }
    }
    if (pending.intent === 'open-modal') {
      handleOpenExportModal();
    } else {
      await handleExportFromModal(pending.format);
    }
  };

  // Delete
  const handleDelete = (doc: FileUploadState & { source: string }) => {
    if (selectedFileId === doc.id) setSelectedFileId(null);
    if (doc.source === 'pending') {
      removeFile(doc.id);
    } else if (doc.invoiceId) {
      deleteInvoice(doc.invoiceId).catch(() => {
        toast.error('Failed to delete — refresh to see it again');
      });
    }
  };

  const handleBulkDelete = () => {
    if (checkedIds.size === 0) return;
    const count = checkedIds.size;
    setConfirmDelete({
      count,
      action: () => {
        if (selectedFileId && checkedIds.has(selectedFileId)) setSelectedFileId(null);
        const toDelete = allDocuments.filter(d => checkedIds.has(d.id));
        toDelete.filter(d => d.source === 'pending').forEach(d => removeFile(d.id));
        const invoicesToDelete = toDelete.filter(d => d.source === 'invoice' && d.invoiceId);
        Promise.all(invoicesToDelete.map(d => deleteInvoice(d.invoiceId!))).catch(() => {
          toast.error('Some documents could not be deleted');
        });
        toast.success(`Deleted ${count} document${count !== 1 ? 's' : ''}`);
        setCheckedIds(new Set());
        setConfirmDelete(null);
      },
    });
  };

  // Helpers
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />;
      case 'processing': return <ArrowPathIcon className="h-4 w-4 text-yellow-500 animate-spin flex-shrink-0" />;
      case 'error': return <XCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />;
    }
  };

  const getVendorName = (doc: FileUploadState) => {
    const data = doc.editedData || doc.result?.data;
    return data?.vendor?.name || data?.company_name || null;
  };

  const getTotal = (doc: FileUploadState) => {
    const data = doc.editedData || doc.result?.data;
    return data?.total_amount;
  };

  const pendingCount = allDocuments.filter(d => d.status === 'pending' || d.status === 'processing').length;
  const completedCount = allDocuments.filter(d => d.status === 'completed').length;
  const errorCount = allDocuments.filter(d => d.status === 'error').length;

  // What's checked — pending vs completed

  const filterTabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'pending', label: 'Pending', count: pendingCount },
    { key: 'completed', label: 'Done', count: completedCount },
    { key: 'error', label: 'Errors', count: errorCount },
  ];

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
      {/* Top action slot: Process button when there are pending files. Uploads
          are handled by the main dropzone in WorkspacePage, so this slot
          collapses when there's nothing to process. */}
      {filter === 'pending' && pendingCount > 0 && !hasProcessing && onProcessFiles && (
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={onProcessFiles}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold transition-colors"
          >
            <PlayIcon className="h-5 w-5" />
            <span className="text-sm">
              {checkedIds.size > 0 && allDocuments.some(d => checkedIds.has(d.id) && d.status === 'pending')
                ? `Process ${allDocuments.filter(d => checkedIds.has(d.id) && d.status === 'pending').length} Selected`
                : `Process All (${pendingCount})`
              }
            </span>
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="px-3 pt-2 pb-1">
        <div className="flex gap-1">
          {filterTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setFilter(tab.key); setSelectedFileId(null); setCheckedIds(new Set()); }}
              className={`flex-1 text-xs font-medium py-1.5 px-1 rounded-lg transition-colors whitespace-nowrap ${
                filter === tab.key
                  ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/40'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 text-[10px] opacity-70">({tab.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk actions bar — hidden when files are processing */}
      {filteredDocuments.length > 0 && !hasProcessing && (
        <div className="px-3 py-1.5 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
          <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500 focus:ring-offset-0"
            />
            {checkedIds.size > 0 ? `${checkedIds.size} selected` : 'Select all'}
          </label>
          {checkedIds.size > 0 && (
            <div className="flex items-center gap-3">
              {filter === 'completed' && (
                <button
                  onClick={handleOpenExportModal}
                  disabled={isExporting !== null}
                  className="flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  {isExporting !== null ? 'Exporting…' : 'Export'}
                </button>
              )}
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {invoicesLoading && invoices.length === 0 ? (
          <div className="space-y-2 p-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="p-3 rounded-xl animate-pulse">
                <div className="flex items-start gap-2.5">
                  <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No {filter} documents
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Upload invoices to get started
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredDocuments.map(doc => {
              const isActive = selectedFileId === doc.id;
              const isChecked = checkedIds.has(doc.id);
              const vendor = getVendorName(doc);
              const total = getTotal(doc);

              return (
                <div
                  key={doc.id}
                  onClick={() => {
                    // Both 'completed' and 'error' (write-back rejected) rows
                    // open the edit view. Switching away with unsaved changes
                    // prompts the clerk first in either case.
                    const canOpen = doc.status === 'completed' || doc.status === 'error';
                    if (canOpen && selectedFileId && selectedFileId !== doc.id && formDirtyRef.current) {
                      setConfirmSwitch({ targetId: doc.id });
                      return;
                    }
                    // Skip interaction for processing files
                    if (doc.status === 'processing') return;
                    // Row click only adds to selection — unchecking happens on the checkbox itself
                    setCheckedIds(prev => {
                      if (prev.has(doc.id)) return prev;
                      const next = new Set(prev);
                      next.add(doc.id);
                      return next;
                    });
                    if (canOpen) {
                      setSelectedFileId(doc.id);
                    }
                  }}
                  className={`group relative p-3 rounded-xl transition-all duration-150 ${
                    (doc.status === 'completed' || doc.status === 'error') ? 'cursor-pointer' : 'cursor-default'
                  } ${
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-700 shadow-sm'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border border-transparent'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {doc.status !== 'processing' ? (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onClick={(e) => toggleCheck(e, doc.id)}
                        onChange={() => {}}
                        className="w-3.5 h-3.5 mt-0.5 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500 focus:ring-offset-0 cursor-pointer flex-shrink-0"
                      />
                    ) : (
                      <div className="w-3.5 flex-shrink-0" />
                    )}

                    {/* Status icon */}
                    {getStatusIcon(doc.status)}

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {doc.file.name}
                        </p>
                        {doc.submitResultKey && (
                          <span
                            title={`Submitted to Keystone${doc.submittedAt ? ` on ${new Date(doc.submittedAt).toLocaleString()}` : ''}${doc.submitResultKey ? ` (key: ${doc.submitResultKey})` : ''}`}
                            className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
                          >
                            <CheckIcon className="h-2.5 w-2.5" />
                            Sent
                          </span>
                        )}
                      </div>
                      {vendor && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {vendor}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {total != null && (
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                            ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    </div>


                    {/* Delete button on hover — hidden while processing */}
                    {doc.status !== 'processing' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
                        title="Delete"
                      >
                        <TrashIcon className="h-3.5 w-3.5 text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
        {allDocuments.length} document{allDocuments.length !== 1 ? 's' : ''}
        {completedCount > 0 && ` · ${completedCount} completed`}
      </div>

      {/* Error modal */}
      {errorModal && (
        <ErrorModal
          title={errorModal.title}
          errors={errorModal.errors}
          onClose={() => setErrorModal(null)}
        />
      )}

      {/* Unsaved changes warning modal */}
      {confirmSwitch && (
        <ConfirmModal
          title="Unsaved Changes"
          message="You have unsaved changes. If you switch documents, your changes will be lost."
          confirmLabel="Discard Changes"
          cancelLabel="Go Back"
          destructive
          onConfirm={() => {
            formDirtyRef.current = false;
            setSelectedFileId(confirmSwitch.targetId);
            setConfirmSwitch(null);
          }}
          onCancel={() => setConfirmSwitch(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <ConfirmModal
          title="Delete Documents"
          message={`Delete ${confirmDelete.count} document${confirmDelete.count !== 1 ? 's' : ''}? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          onConfirm={confirmDelete.action}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Export modal — batch due date override + format choice */}
      <ExportModal
        isOpen={isExportModalOpen}
        invoiceCount={allDocuments.filter(d => checkedIds.has(d.id) && d.status === 'completed').length}
        isExporting={isExporting}
        onExport={handleExportFromModal}
        onCancel={() => setIsExportModalOpen(false)}
      />

      {/* Unsaved changes warning on export */}
      {pendingExport && (
        <ConfirmModal
          title="Unsaved Changes"
          message="You have unsaved changes. Save first, then continue to export?"
          confirmLabel="Save and Continue"
          cancelLabel="Cancel"
          destructive
          onConfirm={handleSaveAndContinueExport}
          onCancel={() => setPendingExport(null)}
        />
      )}
    </div>
  );
};

export default DocumentSidebar;
